import { PhotoTrigger } from '../photo-viewer.tsx';
import { useId } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { formatCents } from '@shared/calculations/pricing.ts';
import type { PhotoIntakeStatus, PhotoQuoteRiskLevel } from '@shared/photo-quotes.ts';
import type { PhotoIntakeHomeSummary } from '@shared/types/photo-quotes.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import { classes } from '../ui.tsx';

/**
 * The small pieces every photo-quote screen shares: the risk badge, the
 * answer clock, the one-line home summary, and the photo viewer. The queue,
 * the review page and the sample house all draw a submission the same way,
 * so an owner who learned one reads the others.
 */

const RISK_TONE: Record<PhotoQuoteRiskLevel, string> = {
  low: 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
  medium: 'border-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)] text-[color:var(--color-ink)]',
  high: 'border-[color:var(--color-danger)] bg-[color:var(--color-danger-soft)] text-[color:var(--color-danger)]',
};

/** Green, amber or red. The word carries the meaning; the color only repeats it. */
export function RiskBadge({ level }: { level: PhotoQuoteRiskLevel | null }) {
  const { t } = useTranslation();
  if (!level) return null;
  return <span className={classes('badge', RISK_TONE[level])}>{t(`photoQuotes.risk.${level}`)}</span>;
}

/** The current time, ticking, for clocks that count down on screen. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

/** Statuses where the customer is waiting on the office. */
const CLOCK_RUNNING: readonly PhotoIntakeStatus[] = ['analyzing', 'awaiting_owner'];

/**
 * How long the customer has been promised an answer within: minutes while it
 * is close, the time of day when it is not (a submission made overnight is
 * due after the next opening), and how late once it is past.
 */
export function SlaTimer({ dueAt, status }: { dueAt: string | null; status: PhotoIntakeStatus }) {
  const { t } = useTranslation();
  const now = useNow();
  if (!dueAt || !CLOCK_RUNNING.includes(status)) return null;
  const minutes = Math.round((Date.parse(dueAt) - now) / 60_000);
  if (minutes < 0) {
    return (
      <span className="badge border-[color:var(--color-danger)] text-[color:var(--color-danger)]">
        {t('photoQuotes.sla.overdue', { count: -minutes })}
      </span>
    );
  }
  if (minutes > 120) {
    const time = new Intl.DateTimeFormat(getDateLocale(), {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(dueAt));
    return <span className="badge text-[color:var(--color-muted)]">{t('photoQuotes.sla.dueAt', { time })}</span>;
  }
  return (
    <span
      className={classes(
        'badge',
        minutes <= 5
          ? 'border-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)]'
          : 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]',
      )}
    >
      {t('photoQuotes.sla.left', { count: minutes })}
    </span>
  );
}

/** "2 bedrooms · 1 bathroom · about 1,100 to 1,300 sq ft · heavy". */
export function homeLine(t: TFunction, home: PhotoIntakeHomeSummary | null): string | null {
  if (!home) return null;
  const number = new Intl.NumberFormat(getDateLocale());
  const parts = [
    home.bedrooms > 0 ? t('photoQuotes.home.bedrooms', { count: home.bedrooms }) : null,
    home.bathrooms > 0 ? t('photoQuotes.home.bathrooms', { count: home.bathrooms }) : null,
    home.squareFeet
      ? t('photoQuotes.home.squareFeet', {
          low: number.format(home.squareFeet.low),
          high: number.format(home.squareFeet.high),
        })
      : null,
    home.conditionCode ? t(`room.condition.${home.conditionCode}`) : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export function money(cents: number, currencyCode: string): string {
  return formatCents(cents, currencyCode, getDateLocale());
}

export interface ViewerPhoto {
  id: string;
  src: string;
  label: string;
  /** Kept out of the read; blurred until somebody chooses to look. */
  hidden: boolean;
  /** Why it was kept out (a person, a document, a repeat, not the home), for the viewer's notice. */
  hiddenReason?: string | null;
}

/**
 * A photo strip that opens into a viewer. Hidden photos (a person, a
 * document, a repeat, not the home) stay blurred in the strip and say why
 * before they are shown large.
 */
export function PhotoGrid({ photos, size = 'large' }: { photos: ViewerPhoto[]; size?: 'small' | 'large' }) {
  const { t } = useTranslation();
  const group = useId();
  const thumb = size === 'small' ? 'h-16 w-16' : 'h-28 w-28 sm:h-32 sm:w-32';
  return (
    <>
      <ul className="flex flex-wrap gap-2">
        {photos.map((photo) => (
          <li key={photo.id}>
            <PhotoTrigger
              src={photo.src}
              alt={photo.label}
              group={group}
              notice={
                photo.hidden
                  ? photo.hiddenReason
                    ? t(`photoQuotes.photos.hiddenReason.${photo.hiddenReason}`, {
                        defaultValue: t('photoQuotes.photos.hiddenNotice'),
                      })
                    : t('photoQuotes.photos.hiddenNotice')
                  : undefined
              }
              className="relative block overflow-hidden rounded border border-[color:var(--color-line)]"
            >
              <img
                src={photo.src}
                alt={photo.label}
                loading="lazy"
                className={classes(thumb, 'object-cover', photo.hidden ? 'blur-md' : null)}
              />
              <span className="absolute bottom-0 left-0 bg-[color:var(--color-ink)]/70 px-1 text-xs text-white">
                {photo.label}
              </span>
            </PhotoTrigger>
          </li>
        ))}
      </ul>
    </>
  );
}
